import { FlowGrid, InfoSheetPanel, Paragraph, useOverlay } from '@fs/zion-ui'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react'
import {
  getNestedOrFloatingElementById,
  useSlsStateContext,
  getNestedSubelementsByType,
  getPersonName,
} from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext, useR2DispatchContext } from '../../../../providers/r2Provider/R2Context'
import { EssentialInformationCard } from '../../../../components/generic/essentialInformationCard'
import RelationshipsCardSwitch, {
  useRelationshipsSectionVisible,
} from '../../../../components/generic/relationshipsCard/RelationshipsCardSwitch'
import { stepperInitialState, stepperReducer, STEPS } from '../../../../utils/stepperReducer'
import EventsCard from '../../../../components/generic/eventsCard/EventsCard'
import AdditionalFactsCard from '../../../../components/generic/additionalFactsCard/AdditionalFactsCard'
import { redirectToSourceLinker } from './utils/utils'
import { getAddNamesEntryPoint } from '../../../../hooks/useAddNamesEntryPoint'
import { ReviewCard } from '../../../../components/generic/reviewCard/ReviewCard'
import useUnsavedChangesOverlay from '../../../../hooks/useUnsavedChangesOverlay'
import UnsavedChangesDialog from '../../../../components/generic/unsavedChangesDialog/UnsavedChangesDialog'
import { useDisplayHighlights } from '../../../../hooks/useDisplayHighlights'
import { MAKE_NEW_RECORD } from '../../../../components/generic/shared/relationships/relationshipConstants'

/**
 * AddNamesPanel component for managing the step-by-step process of adding and reviewing person details,
 * including essential information, family members, events, and additional facts. Handles unsaved changes,
 * overlays, and navigation between multiple persons to review.
 *
 * The entry point and the group it is adding to come from `r2State.addNamesIntent`, set by
 * {@link useAddNamesEntryPoint}.
 *
 * @param {Object} props - Component props.
 * @param {string} [props.overlayId='addNamesOverlay'] - Which overlay this instance opens with. Other
 *   instances are mounted elsewhere with a different id, and each only handles the intents addressed to it.
 *
 * @returns {JSX.Element} The rendered panel UI for adding and reviewing person details.
 */
const AddNamesPanel = ({ overlayId = 'addNamesOverlay' }) => {
  const { images, currentImageArk } = useSlsStateContext()
  const overlayContext = useOverlayContext()
  const { closeOverlay } = overlayContext
  // Fetch the overlay state for this panel from the overlay context by its id.
  const overlay = overlayContext[overlayId]
  const { thumbnails } = useSharedBrowserStateContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const [stepperState, stepperDispatch] = useReducer(stepperReducer, stepperInitialState)
  const [personId, setPersonId] = useState(null)
  const [createdRecordId, setCreatedRecordId] = useState(null)

  // Verify if the intent is for this panel, and if so, extract the details from it. Otherwise, clear them.
  const intent = r2State.addNamesIntent
  const intentIsForThisPanel =
    Boolean(intent) &&
    getAddNamesEntryPoint(intent.entryPoint).overlayId === overlayId &&
    intent.imageArk === currentImageArk
  const { entryPoint, recordId } = intentIsForThisPanel ? intent : {}
  const { description, addingGroupMember, title, subtitle, headingColor } = useMemo(
    () => getAddNamesEntryPoint(entryPoint),
    [entryPoint]
  )
  const headerDetails = useMemo(() => ({ title, subtitle, headingColor }), [title, subtitle, headingColor])
  const saveChangesOverlay = useOverlay()
  const [showSaveChangesOverlay, setShowSaveChangesOverlay] = useState(false)
  const scrollRef = useRef(null)
  const splitScreenPartitionKey = r2State.splitScreen ? thumbnails?.[r2State?.referenceFocusedIndex]?.ark : null
  const record = useMemo(() => {
    const id = recordId || createdRecordId
    if (!id) return null
    return getNestedOrFloatingElementById(images, id, true)
  }, [images, recordId, createdRecordId])
  const householdHasOtherPersons = useMemo(
    () => record?.subElements?.some((sub) => sub?.elementType === 'PERSON' && sub?.id !== personId) || false,
    [record, personId]
  )

  // Skip the relationships step when there's only one person. Adding a group member sets the
  // relationship on the card that opened the panel, so we don't need to show it here.
  const relationshipsSectionVisible =
    useRelationshipsSectionVisible(householdHasOtherPersons, true) && !addingGroupMember

  // Retrieve the SLS person object by id
  // on the first load, if no person id is provided, create a new person object
  // Don't set primary here - let prepareEssentialInformationDefaultValues determine it based on household context
  const slsPerson = useMemo(
    () =>
      personId
        ? getNestedOrFloatingElementById(images, personId)
        : { recordId: recordId || createdRecordId || MAKE_NEW_RECORD },
    [images, recordId, createdRecordId, personId]
  )

  const panelIsOpen = Boolean(overlay?.isOpen)

  const closePanel = useCallback(() => {
    // Set the added group members in the R2 state if this was a group add. This is used by the caller to update the group members list after the panel closes.
    if (personId && addingGroupMember) {
      r2Dispatch({
        type: 'SET_ADDED_GROUP_MEMBERS',
        value: {
          recordId,
          persons: [
            {
              id: personId,
              name: slsPerson ? getPersonName({ person: slsPerson }) || '' : '',
              recordId: slsPerson?.recordId ?? null,
            },
          ],
        },
      })
    }
    r2Dispatch({ type: 'SET_ADD_NAMES_INTENT', value: null })
    closeOverlay(overlayId)
  }, [closeOverlay, r2Dispatch, overlayId, personId, slsPerson, addingGroupMember, recordId])

  const handleClosePanel = useCallback(() => {
    if (showSaveChangesOverlay) {
      saveChangesOverlay.open()
    } else {
      closePanel()
    }
  }, [showSaveChangesOverlay, saveChangesOverlay, closePanel])

  const handleDirtyChange = useCallback((isDirty) => {
    setShowSaveChangesOverlay(isDirty)
  }, [])

  useUnsavedChangesOverlay({
    overlay: saveChangesOverlay,
    panelId: overlayId,
    condition: showSaveChangesOverlay,
  })

  // Reset on open rather than on close. The close transition keeps this subtree rendered, so clearing
  // personId on the way out remounts the form and fetches an ARK nobody asked for. Layout effect so the
  // previous person is never painted.
  useLayoutEffect(() => {
    if (panelIsOpen) {
      stepperDispatch({ type: 'RESET' })
      setPersonId(null)
      setCreatedRecordId(null)
    }
  }, [panelIsOpen])

  // The form that fetches the pending person's ark unmounts at the essential-info save, but the later
  // steps still draw highlights for them, so the panel closing is what ends the add. Keyed on isOpen rather
  // than unmount because TranscriptAndNamesPanel keeps this component mounted after close.
  useEffect(() => {
    if (!panelIsOpen) {
      r2Dispatch({ type: 'SET_CREATED_PERSON_ID', value: '' })
    }
  }, [panelIsOpen, r2Dispatch])

  const handleAddAnotherPerson = useCallback(() => {
    stepperDispatch({ type: 'RESET' })
    setPersonId(null)
    setShowSaveChangesOverlay(false)
  }, [])

  const sharedOnSaveCallback = useCallback(
    (currentStep, nextStep) => {
      const actions = [{ type: 'SET_REVIEWED', step: currentStep, value: true }]
      if (stepperState.currentEditStep === currentStep) {
        // Advance to the next step in flow
        actions.push({ type: 'SET_CURRENT_STEP', step: nextStep })
      } else {
        // Close the card and let the reducer figure out what card to open next
        actions.push({ type: 'SET_EDIT_MODE', step: currentStep, value: false })
      }
      stepperDispatch({
        type: 'BATCH',
        actions,
      })

      setShowSaveChangesOverlay(false)
    },
    [stepperState.currentEditStep]
  )

  // Display highlights when all cards are in view mode
  const allCardsInViewMode = useMemo(
    () => Object.values(stepperState.editMode).every((isEditing) => !isEditing),
    [stepperState.editMode]
  )

  const fieldIds = useMemo(() => {
    if (!slsPerson?.id) return []
    const fields = getNestedSubelementsByType(slsPerson, 'FIELD')
    return fields.map((field) => field.id).filter(Boolean)
  }, [slsPerson])

  useDisplayHighlights({
    elementIds: fieldIds,
    enabled: allCardsInViewMode && !!slsPerson?.id, // Only when all cards in view mode and person exists
  })

  return (
    <InfoSheetPanel {...overlay} close={handleClosePanel} {...headerDetails}>
      {/* Using the person id as key to force re-render when person changes, so all child components reset their internal state */}
      <FlowGrid columnCount={1} orientation="horizontal" key={slsPerson?.id}>
        <Paragraph size="sm" ref={scrollRef}>
          {description}
        </Paragraph>
        <EssentialInformationCard
          person={slsPerson}
          reviewed={stepperState.reviewed.essentialInfo}
          editMode={stepperState.editMode[STEPS.ESSENTIAL_INFO]}
          setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.ESSENTIAL_INFO, value })}
          setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.ESSENTIAL_INFO, value })}
          onSave={({ personId: newPersonID, formValues }) => {
            setPersonId(newPersonID)
            if (!recordId && formValues?.recordId) {
              setCreatedRecordId(formValues.recordId)
            }
            sharedOnSaveCallback(STEPS.ESSENTIAL_INFO, STEPS.EVENTS)
          }}
          onDirtyChange={handleDirtyChange}
          saveCardDataToSls
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
        <EventsCard
          person={slsPerson}
          reviewed={stepperState.reviewed.events}
          editMode={stepperState.editMode[STEPS.EVENTS]}
          setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.EVENTS, value })}
          setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.EVENTS, value })}
          onSave={() => {
            sharedOnSaveCallback(STEPS.EVENTS, STEPS.ADDITIONAL_FACTS)
          }}
          saveCardDataToSls
          onDirtyChange={handleDirtyChange}
          showDataInPendingState={false}
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
        <AdditionalFactsCard
          person={slsPerson}
          reviewed={stepperState.reviewed.additionalFacts}
          editMode={stepperState.editMode[STEPS.ADDITIONAL_FACTS]}
          setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.ADDITIONAL_FACTS, value })}
          setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.ADDITIONAL_FACTS, value })}
          onSave={() => {
            const nextStep = relationshipsSectionVisible ? STEPS.FAMILY_MEMBERS : STEPS.REVIEW_ALL
            sharedOnSaveCallback(STEPS.ADDITIONAL_FACTS, nextStep)
          }}
          onDirtyChange={handleDirtyChange}
          saveCardDataToSls
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
        {/* Swap for RelationshipsCard when rip_sharedImageViewer_relationshipsCard is cleaned up. */}
        {relationshipsSectionVisible && (
          <RelationshipsCardSwitch
            householdHasOtherPersons={householdHasOtherPersons}
            person={slsPerson}
            reviewed={stepperState.reviewed.familyMembers}
            editMode={stepperState.editMode[STEPS.FAMILY_MEMBERS]}
            setEditMode={(value) => stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.FAMILY_MEMBERS, value })}
            setReviewed={(value) => stepperDispatch({ type: 'SET_REVIEWED', step: STEPS.FAMILY_MEMBERS, value })}
            onSave={() => {
              sharedOnSaveCallback(STEPS.FAMILY_MEMBERS, STEPS.REVIEW_ALL)
            }}
            isInReviewProcess={false}
            onDirtyChange={handleDirtyChange}
            saveCardDataToSls
            splitScreenPartitionKey={splitScreenPartitionKey}
            // Empties the relationship inputs, so the patron picks one per group member.
            isCreatingNewPerson
            // Reaching this panel was itself a group action; offering more here goes in circles.
            disableGroupChanges
          />
        )}
        <ReviewCard
          active={stepperState.editMode[STEPS.REVIEW_ALL]}
          person={slsPerson}
          splitScreenPartitionKey={splitScreenPartitionKey}
          onAddAnotherPerson={handleAddAnotherPerson}
          closeOnly={addingGroupMember}
          onSave={(action) => {
            if (action === 'attach') {
              const personArk = slsPerson.id || slsPerson.recordId
              redirectToSourceLinker(personArk)
              handleClosePanel()
            } else if (action === 'close') {
              handleClosePanel()
            }
          }}
        />
      </FlowGrid>
      {/* Unsaved Changes Overlay */}
      <UnsavedChangesDialog
        overlay={saveChangesOverlay}
        onExit={() => {
          saveChangesOverlay.close()
          // If there's a pending action, run it after confirming exit, e.g., navigate away or close panel
          if (typeof saveChangesOverlay.pendingAction === 'function') {
            saveChangesOverlay.pendingAction()
          } else {
            closePanel()
          }
          closeOverlay(overlayId)
        }}
      />
    </InfoSheetPanel>
  )
}

AddNamesPanel.propTypes = {
  overlayId: PropTypes.string,
}

AddNamesPanel.defaultProps = {
  overlayId: 'addNamesOverlay',
}

export default AddNamesPanel
